/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import { FormikProps } from "formik";
import {
  TextArea,
  TextInput,
  AIAssistButtons,
  Tooltip,
} from "../../components";

import { MediaDetailConfig } from "@/utils/utilFunctions/Mapper/MediaDetailConfig";
import { MediaEditableConfig } from "@/utils/utilFunctions/Mapper/EditableConfig";
import { AdminPreset } from "@/database";

import { addToHistory, handleHistoryNavigation } from "@/utils/ui/historyUtils";
import { aiService, translateTitleUrl } from "@/store/app/app.actions";
import { store } from "../../store";
import { MediaModel, PresetListModel, ProductModel } from "@/database";

import Save from "../../assets/icons/svg/save.svg?react";
import Edit from "../../assets/icons/svg/edit.svg?react";
import Cancel from "../../assets/icons/svg/cancel.svg?react";
import { hasNonEnglishAlphabeticChars } from "@/utils/mediautils/CommonFunctions";
import {
  shouldShowField,
  isFieldMandatoryLoose,
} from "@/utils/mediautils/policyHelpers";
import { MEDIA_STATUS } from "@/utils/constants/uiConstants";

interface TitleAndDescProps {
  formik: FormikProps<any>;
  currentMediaDetailsForEdit: MediaModel;
  mediaDetailConfig: ReturnType<typeof MediaDetailConfig>;
  mediaEditableConfig: ReturnType<typeof MediaEditableConfig>;
  isWizard?: boolean;
  metaPresetData?: AdminPreset;
  productDetailsFromConstant: any;
  userEditedTitleUrl: boolean;
  setUserEditedTitleUrl: (value: boolean) => void;
  isTitleUrlLoading: {
    title: boolean;
    description: boolean;
    customPrompt: boolean;
    titleUrl: boolean;
  };
  setIsTitleUrlLoading: React.Dispatch<
    React.SetStateAction<{
      title: boolean;
      description: boolean;
      customPrompt: boolean;
      titleUrl: boolean;
    }>
  >;
  disabled?: boolean;
  selectedProduct?: ProductModel | null;
  isContentType2?: boolean;
}

const TitleAndDesc: React.FC<TitleAndDescProps> = ({
  formik,
  currentMediaDetailsForEdit,
  mediaDetailConfig,
  mediaEditableConfig,
  isWizard,
  metaPresetData,
  productDetailsFromConstant,
  userEditedTitleUrl,
  setUserEditedTitleUrl,
  isTitleUrlLoading,
  setIsTitleUrlLoading,
  disabled = false,
  selectedProduct,
  isContentType2 = false,
}) => {
  const [activeField, setActiveField] = useState<
    "title" | "description" | null
  >(null);
  const [isEdit, setIsEdit] = useState<boolean>(true);
  const [customPrompt, setCustomPrompt] = useState({
    title: { open: false, text: "", isSending: false },
    description: { open: false, text: "", isSending: false },
  });
  const [descriptionHistory, setDescriptionHistory] = useState<string[]>([]);
  const [titleHistory, setTitleHistory] = useState<string[]>([]);
  const [currentDescriptionHistoryIndex, setCurrentDescriptionHistoryIndex] =
    useState(-1);
  const [currentTitleHistoryIndex, setCurrentTitleHistoryIndex] = useState(-1);
  const [isDescriptionBlurLoading, setIsDescriptionBlurLoading] =
    useState(false);
  const [isDescriptionAiButtonLoading, setIsDescriptionAiButtonLoading] =
    useState(false);
  const [isTitleAiButtonLoading, setIsTitleAiButtonLoading] = useState(false);
  const [isTitleBlurLoading, setIsTitleBlurLoading] = useState(false);
  const [isExcecuteDescAiTrans, setIsExcecuteDescAiTrans] =
    useState<boolean>(false);
  const [isExcecuteTitleAiTrans, setIsExcecuteTitleAiTrans] =
    useState<boolean>(false);

  const handleTitleHistoryNavigation = (direction: "back" | "forward") => {
    handleHistoryNavigation(
      direction,
      titleHistory,
      currentTitleHistoryIndex,
      setCurrentTitleHistoryIndex,
      "title",
      formik
    );
  };

  const handleDescriptionHistoryNavigation = (
    direction: "back" | "forward"
  ) => {
    handleHistoryNavigation(
      direction,
      descriptionHistory,
      currentDescriptionHistoryIndex,
      setCurrentDescriptionHistoryIndex,
      "desc",
      formik
    );
  };

  const prevTitleRef = useRef<string>(formik.values.title);
  useEffect(() => {
    // Only sync if title has actually changed (not on initial mount)
    if (
      prevTitleRef.current !== formik.values.title &&
      formik.values.title !== "" &&
      !userEditedTitleUrl &&
      currentMediaDetailsForEdit?.media_status !==
        MEDIA_STATUS.MEDIA_STATUS_PUBLISHED
    ) {
      // Break syncing if title contains non-English alphabetic characters
      if (hasNonEnglishAlphabeticChars(formik.values.title)) {
        prevTitleRef.current = formik.values.title;
        return;
      }

      // Resume syncing when title doesn't have non-English characters
      // This will sync even if non-English characters were just removed
      formik.setFieldValue("title_url", formik.values.title, false);
      formik.setFieldValue("title_url_edited", formik.values.title, false);
      prevTitleRef.current = formik.values.title;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.title,
    userEditedTitleUrl,
    currentMediaDetailsForEdit?.media_status,
  ]);

  useEffect(() => {
    const handleMetaPresetAiLoading = (event: Event) => {
      const customEvent = event as CustomEvent<{
        field: string;
        loading: boolean;
      }>;
      const { field, loading } = customEvent.detail || {};

      if (field === "title") {
        setIsTitleBlurLoading(loading);
      }

      if (field === "description") {
        setIsDescriptionBlurLoading(loading);
      }
    };

    window.addEventListener("metaPresetAiLoading", handleMetaPresetAiLoading);

    return () => {
      window.removeEventListener(
        "metaPresetAiLoading",
        handleMetaPresetAiLoading
      );
    };
  }, []);

  const handleCustomPromptClick = (field: "title" | "description") => {
    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { ...prev[field], open: true },
    }));
  };

  const handleCustomPromptClose = (field: "title" | "description") => {
    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { open: false, text: "" },
    }));
  };

  const handleCustomPromptTextChange = (
    field: "title" | "description",
    text: string
  ) => {
    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { ...prev[field], text },
    }));
  };

  const handlePromptSend = async (field: "title" | "description") => {
    if (!customPrompt[field].text.trim() || customPrompt[field].isSending)
      return;

    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { ...prev[field], isSending: true },
    }));

    try {
      const response = await store.dispatch(
        aiService({
          title: formik.values[field === "description" ? "desc" : "title"],
          id: currentMediaDetailsForEdit.id,
          service: "prompt",
          target_language: customPrompt[field].text,
        })
      );

      if (response) {
        const aiResponse = response.output_processed.output || "";

        if (field === "title") {
          if (
            titleHistory.length === 0 ||
            titleHistory[titleHistory.length - 1] !== formik.values.title
          ) {
            addToHistory(
              formik.values.title,
              setTitleHistory,
              setCurrentTitleHistoryIndex
            );
          }
          formik.setFieldValue("title", aiResponse);
          addToHistory(
            aiResponse,
            setTitleHistory,
            setCurrentTitleHistoryIndex
          );
        } else {
          if (
            descriptionHistory.length === 0 ||
            descriptionHistory[descriptionHistory.length - 1] !==
              formik.values.desc
          ) {
            addToHistory(
              formik.values.desc,
              setDescriptionHistory,
              setCurrentDescriptionHistoryIndex
            );
          }
          formik.setFieldValue("desc", aiResponse);
          addToHistory(
            aiResponse,
            setDescriptionHistory,
            setCurrentDescriptionHistoryIndex
          );
        }

        setCustomPrompt((prev) => ({
          ...prev,
          [field]: { ...prev[field], text: "", isSending: false },
        }));
      }
    } catch (error) {
      console.error("Error in AI service:", error);
      setCustomPrompt((prev) => ({
        ...prev,
        [field]: { ...prev[field], text: "", isSending: false },
      }));
    }
  };

  const handleDescriptionBlur = async (
    e: React.FocusEvent<HTMLTextAreaElement>
  ) => {
    formik.handleBlur(e);
    if (!isWizard) return;
    if (isExcecuteDescAiTrans) return;

    if (formik.values.desc.trim() && metaPresetData?.description_ai_transform) {
      if (
        descriptionHistory.length === 0 ||
        descriptionHistory[descriptionHistory.length - 1] !== formik.values.desc
      ) {
        addToHistory(
          formik.values.desc,
          setDescriptionHistory,
          setCurrentDescriptionHistoryIndex
        );
      }

      setIsDescriptionBlurLoading(true);
      const targetText =
        metaPresetData?.description_ai_transform === "Custom Prompt"
          ? metaPresetData?.description_custom_prompt
          : metaPresetData?.description_ai_transform || "";
      try {
        const response = await store.dispatch(
          aiService({
            title: formik.values.desc,
            id: currentMediaDetailsForEdit.id,
            service: "prompt",
            target_language: targetText,
            type: "description",
          })
        );

        if (response) {
          const aiResponse = response.output_processed.output || "";
          formik.setFieldValue("desc", aiResponse);
          addToHistory(
            aiResponse,
            setDescriptionHistory,
            setCurrentDescriptionHistoryIndex
          );
          setIsExcecuteDescAiTrans(true);
        }
      } catch (error) {
        console.error("Error in AI service on description blur:", error);
      } finally {
        setIsDescriptionBlurLoading(false);
      }
    }
  };

  const handleSaveTitleUrlEdited = async () => {
    if (formik.values.title_url_edited !== "") {
      setIsTitleUrlLoading((prev) => ({
        ...prev,
        titleUrl: true,
      }));
      try {
        const response = await store.dispatch(
          translateTitleUrl({
            title: formik.values.title_url,
            id: currentMediaDetailsForEdit.id,
            editedTitleUrl: userEditedTitleUrl,
            titleUrl: formik.values.title_url,
          })
        );
        if (response) {
          formik.setFieldValue("title_url", response);
          formik.setFieldValue("title_url_edited", response);
        }
      } catch (error) {
        console.error("Error translating title:", error);
      } finally {
        setIsTitleUrlLoading((prev) => ({
          ...prev,
          titleUrl: false,
        }));
      }
    }
  };

  console.log("metaPresetData ---->", metaPresetData);

  const handleTitleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);

    if (isExcecuteTitleAiTrans) return;

    if (
      formik.values.title.trim() &&
      metaPresetData?.title_ai_transform &&
      isWizard
    ) {
      if (
        titleHistory.length === 0 ||
        titleHistory[titleHistory.length - 1] !== formik.values.title
      ) {
        addToHistory(
          formik.values.title,
          setTitleHistory,
          setCurrentTitleHistoryIndex
        );
      }

      setIsTitleBlurLoading(true);
      const targetText =
        metaPresetData?.title_ai_transform === "Custom Prompt"
          ? metaPresetData?.title_custom_prompt
          : metaPresetData?.title_ai_transform || "";
      try {
        const response = await store.dispatch(
          aiService({
            title: formik.values.title,
            id: currentMediaDetailsForEdit.id,
            service: "prompt",
            target_language: targetText,
            type: "title",
          })
        );

        if (response) {
          const aiResponse = response.output_processed.output || "";
          formik.setFieldValue("title", aiResponse);
          addToHistory(
            aiResponse,
            setTitleHistory,
            setCurrentTitleHistoryIndex
          );
          setIsExcecuteTitleAiTrans(true);
        }
      } catch (error) {
        console.error("Error in AI service on Title blur:", error);
      } finally {
        setIsTitleBlurLoading(false);
      }
    }
  };

  return (
    <>
      <div className="d-flex flex-column gap-2">
        {customPrompt.title.open && (
          <TextArea
            label="Custom Prompt"
            name="customPrompt"
            showRequired={false}
            placeholder="Enter your custom prompt..."
            value={customPrompt.title.text}
            onChange={(e) =>
              handleCustomPromptTextChange("title", e.target.value)
            }
            onBlur={formik.handleBlur}
            error={
              customPrompt.title.text.length > 500
                ? "Prompt should be less than 500 characters"
                : undefined
            }
            touched={customPrompt.title.text.length > 500}
            characterLimit={500}
            onSend={() => handlePromptSend("title")}
            isSending={customPrompt.title.isSending}
            style={{
              marginBottom: "1rem",
              backgroundColor: "#fff",
            }}
          />
        )}
        {mediaDetailConfig.title.enabled && (
          <div className="position-relative">
            <TextInput
              name="title"
              placeholder="Enter title here..."
              disabled={disabled || !mediaEditableConfig.title}
              onChange={formik.handleChange}
              onBlur={handleTitleBlur}
              value={formik.values.title}
              touched={!!formik.touched.title}
              error={
                typeof formik.errors.title === "string"
                  ? formik.errors.title
                  : undefined
              }
              label="Title"
              showRequired={mediaDetailConfig.title.required}
              characterLimit={100}
              style={{ paddingRight: "40px" }}
              rightElement={
                <div className="d-flex align-items-center gap-2">
                  {isTitleBlurLoading ? (
                    <div className="d-flex align-items-center">
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <AIAssistButtons
                      field="title"
                      isWizard={!!isWizard}
                      isOpen={activeField === "title"}
                      setisDescriptionAiButtonLoading={(flag: boolean) => {
                        setIsTitleAiButtonLoading(flag);
                        setIsExcecuteTitleAiTrans(true);
                      }}
                      historyFromProps={titleHistory}
                      currentHistoryIndex={currentTitleHistoryIndex}
                      onHistoryNavigation={handleTitleHistoryNavigation}
                      onAddToHistory={(text: string) =>
                        addToHistory(
                          text,
                          setTitleHistory,
                          setCurrentTitleHistoryIndex
                        )
                      }
                      setHistory={setTitleHistory}
                      setIndex={setCurrentTitleHistoryIndex}
                      formik={formik}
                      onToggle={() =>
                        setActiveField(activeField === "title" ? null : "title")
                      }
                      textToProcess={formik.values.title}
                      mediaId={currentMediaDetailsForEdit.id}
                      onResponseReceived={(response) => {
                        formik.setFieldValue("title", response);
                        addToHistory(
                          response,
                          setTitleHistory,
                          setCurrentTitleHistoryIndex
                        );
                      }}
                      onCustomPromptClick={() =>
                        handleCustomPromptClick("title")
                      }
                      customPromptOpen={customPrompt.title.open}
                      onCustomPromptClose={() =>
                        handleCustomPromptClose("title")
                      }
                    />
                  )}
                </div>
              }
            />
          </div>
        )}
      </div>
      {!isContentType2 &&
        mediaDetailConfig.title_url.enabled &&
        shouldShowField(selectedProduct?.title_for_url_policy) && (
          <div tabIndex={-1} className="position-relative">
            {isTitleUrlLoading.titleUrl && (
              <div
                className="spinner-border spinner-border-sm text-primary position-absolute top-0 end-0 me-3"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
            <div className="title_url">
              <label className="me-2">
                Title for URL:
                {mediaDetailConfig.title_url.required &&
                  isFieldMandatoryLoose(
                    selectedProduct?.title_for_url_policy
                  ) && <small className="required ms-1">*</small>}
              </label>
              {isEdit ? (
                <div className="position-relative">
                  <div className="input-group mt-1" style={{ width: "100%" }}>
                    <input
                      type="text"
                      name="title_url_edited"
                      className={`form-control pe-5 ${
                        formik.errors.title_url_edited &&
                        formik.touched.title_url_edited
                          ? "is-invalid"
                          : ""
                      }`}
                      style={{ width: "100%" }}
                      value={formik.values.title_url_edited || ""}
                      onChange={(e) => {
                        formik.setFieldValue(
                          "title_url_edited",
                          e.target.value
                        );
                        formik.setFieldValue("title_url", e.target.value);
                        setUserEditedTitleUrl(true);
                      }}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.touched.title_url_edited &&
                    formik.errors.title_url_edited && (
                      <div
                        className="invalid-feedback d-block"
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          marginTop: "0.25rem",
                        }}
                      >
                        {typeof formik.errors.title_url_edited === "string"
                          ? formik.errors.title_url_edited
                          : "Title for URL is required"}
                      </div>
                    )}
                </div>
              ) : (
                <span>{formik.values.title_url_edited || ""}</span>
              )}
              {isEdit ? (
                <div className="position-absolute end-0 bottom-0 border-start mb-2 me-2 z-5 px-2">
                  {isTitleUrlLoading.titleUrl ? (
                    <div
                      onClick={() => handleSaveTitleUrlEdited()}
                      className="pointer"
                    >
                      <i className="bi bi-translate f-20"></i>
                    </div>
                  ) : (
                    <Tooltip content="Translate to English">
                      <div
                        onClick={() => handleSaveTitleUrlEdited()}
                        className="pointer"
                      >
                        <i className="bi bi-translate f-20"></i>
                      </div>
                    </Tooltip>
                  )}
                  <div onClick={() => setIsEdit(false)} className="pointer H">
                    <Cancel style={{ width: "20px", height: "20px" }} />
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setIsEdit(true)}
                  className="pointer anchor ms-2 ps-2 border-start url-action"
                >
                  <Edit style={{ width: "15px", height: "15px" }} /> Edit
                </div>
              )}
            </div>
          </div>
        )}
      <div className="">
        {customPrompt.description.open && (
          <TextArea
            label="Custom Prompt"
            name="customPrompt"
            showRequired={false}
            placeholder="Enter your custom prompt..."
            value={customPrompt.description.text}
            onChange={(e) =>
              handleCustomPromptTextChange("description", e.target.value)
            }
            onBlur={formik.handleBlur}
            error={
              customPrompt.description.text.length > 500
                ? "Prompt should be less than 500 characters"
                : undefined
            }
            touched={customPrompt.description.text.length > 500}
            characterLimit={500}
            onSend={() => handlePromptSend("description")}
            isSending={customPrompt.description.isSending}
            style={{
              marginBottom: "1rem",
              backgroundColor: "#fff",
            }}
          />
        )}
        {mediaDetailConfig.desc.enabled &&
          shouldShowField(selectedProduct?.desc_policy) && (
            <div className="position-relative flex-grow-1">
              <TextArea
                name="desc"
                placeholder="Description here..."
                label="Description"
                onBlur={handleDescriptionBlur}
                error={
                  typeof formik.errors.desc === "string"
                    ? formik.errors.desc
                    : undefined
                }
                touched={!!formik.touched.desc}
                disabled={disabled || !mediaEditableConfig.desc}
                onChange={formik.handleChange}
                showRequired={
                  isContentType2
                    ? false
                    : mediaDetailConfig.desc.required &&
                      isFieldMandatoryLoose(selectedProduct?.desc_policy)
                }
                value={formik.values.desc}
                style={{
                  height: "90px",
                  resize: "none",
                  lineHeight: "20px",
                }}
                characterLimit={5000}
                rightElement={
                  <div className="d-flex align-items-center gap-2">
                    {isDescriptionBlurLoading ? (
                      <div className="d-flex align-items-center">
                        <div
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <AIAssistButtons
                        field="description"
                        isWizard={!!isWizard}
                        isOpen={activeField === "description"}
                        setisDescriptionAiButtonLoading={(flag: boolean) => {
                          setIsDescriptionAiButtonLoading(flag);
                          setIsExcecuteDescAiTrans(true);
                        }}
                        historyFromProps={descriptionHistory}
                        currentHistoryIndex={currentDescriptionHistoryIndex}
                        onHistoryNavigation={handleDescriptionHistoryNavigation}
                        onAddToHistory={(text: string) =>
                          addToHistory(
                            text,
                            setDescriptionHistory,
                            setCurrentDescriptionHistoryIndex
                          )
                        }
                        setHistory={setDescriptionHistory}
                        setIndex={setCurrentDescriptionHistoryIndex}
                        formik={formik}
                        onToggle={() =>
                          setActiveField(
                            activeField === "description" ? null : "description"
                          )
                        }
                        textToProcess={formik.values.desc}
                        mediaId={currentMediaDetailsForEdit.id}
                        onResponseReceived={(response) => {
                          setIsDescriptionAiButtonLoading(false);
                          setIsTitleUrlLoading((prev) => ({
                            ...prev,
                            description: true,
                          }));
                          formik.setFieldValue("desc", response);
                          addToHistory(
                            response,
                            setDescriptionHistory,
                            setCurrentDescriptionHistoryIndex
                          );
                          setIsTitleUrlLoading((prev) => ({
                            ...prev,
                            description: false,
                          }));
                          setIsDescriptionAiButtonLoading(false);
                        }}
                        onCustomPromptClick={() =>
                          handleCustomPromptClick("description")
                        }
                        customPromptOpen={customPrompt.description.open}
                        onCustomPromptClose={() =>
                          handleCustomPromptClose("description")
                        }
                      />
                    )}
                  </div>
                }
              />
            </div>
          )}
      </div>
    </>
  );
};

export default TitleAndDesc;
